import React, { useState, useEffect, useCallback, useRef } from "react";
import { Auth } from "aws-amplify";
import { jwtDecode } from "jwt-decode";
import { useNavigate, useLocation } from "react-router-dom";
import { useLoading } from "../../../context/LoadingContext";
import { getParameterFromManager } from "../../../services/Utils";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import DataTable from "../../Requirement/components/common/ReusableTable";
import SearchFilter from "../../Requirement/components/common/SearchFilter";
import AlertModal from "../../Requirement/components/common/AlerModel";
import {
  fetchData,
  postQuery,
  searchfetchQueries,
} from "../../Requirement/services/RequirementServices";
import ProformaInvoiceForm from "./ProformaInvoiceForm";
import { openProformaInvoicePopup } from "../../../services/ProformaInvoiceTemplate";

// Define default form data to ensure consistency
const defaultFormData = {
  InvoiceNo: "",
  ProjectID: "",
  ProductName: "",
  Grade: "",
  HSCode: "",
  Quantity: "0",
  InvoiceDate: "",
  StrategicPartnerID: "",
  StrategicPartner: "",
  DispatchFrom: "",
  ShipTo: "",
  PlaceOfSupply: "",
  PO: "",
  EInvoiceAckNo: "",
  EInvoiceDate: "",
  PackagingNote: "",
  Unit: "MT",
  PricePerUnit: "0",
  SubTotal: "0",
  IgstPercent: "5",
  CgstPercent: "0",
  SgstPercent: "0",
  TotalAmount: "0",
  EwayBillNo: "",
  VehicleNo: "",
  EInvoiceIRN: "",
};

const ProformaInvoices = () => {
  const { setLoading } = useLoading();
  const location = useLocation();
  const navigate = useNavigate();
  const [rowStart, setRowStart] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [totalItems, setTotalItems] = useState(0);
  const [invoices, setInvoices] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [activeUser, setActiveUser] = useState("");
  const [allProjectList, setAllProjectList] = useState([]);
  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    autoClose: null,
  });
  const [searchValues, setSearchValues] = useState({
    searchInvoiceNo: "",
  });
  const formRef = useRef(null);

  const [formData, setFormData] = useState(defaultFormData);
  const [errors, setErrors] = useState({});
  const [productInfo, setProductInfo] = useState([]);

  const handleModalClose = () => {
    setModal((prev) => ({ ...prev, show: false }));
  };

  const handleInvoicePopup = useCallback(
    async (dataInvoice) => {
      console.log("dataInvoice@@@", dataInvoice);
      console.log('@@@SP',dataInvoice.StrategicPartnerID);
      setLoading(true);

      try {
        const selectedProject = allProjectList.find(
          (project) => project.ProjectID === dataInvoice.ProjectID
        );

        let updatedProductInfo = {};
        if (selectedProject && selectedProject.ProductID) {
          // Wait for product info to be fetched
          const response = await fetchData({
            target: "listProducts",
            userOrder: "ProductName",
            rowStart: 0,
            pageSize: 1,
            filterTerms: { ProductID: selectedProject.ProductID },
          });
          updatedProductInfo = response.items[0] || {};
          setProductInfo(updatedProductInfo); // Update state for future use
        }

        let updatedStrategicPartnerInfo = {};
        if (dataInvoice.StrategicPartnerID) {
          const responseSp = await fetchData({
            target: "listAMTPLUsers",
            userOrder: "AMTPLUserID",
            rowStart: 0,
            pageSize: 1,
            filterTerms: { AMTPLUserID: dataInvoice.StrategicPartnerID },
          });
          updatedStrategicPartnerInfo = responseSp.items[0] || {};
        }

        const IgstAmount =
          (dataInvoice.SubTotal * dataInvoice.IgstPercent) / 100;
        const CgstAmount =
          (dataInvoice.SubTotal * dataInvoice.CgstPercent) / 100;
        const SgstAmount =
          (dataInvoice.SubTotal * dataInvoice.SgstPercent) / 100;

          

        const updatedDataInvoice = {
          ...dataInvoice,
          IgstAmount,
          CgstAmount,
          SgstAmount,
          ProductName: updatedProductInfo.ProductName || "",
          Grade: updatedProductInfo.Grade || "",
          HSCode: updatedProductInfo.HsCode || "",
          CompanyName: updatedStrategicPartnerInfo.CompanyName || "",
          CompanyAddress: updatedStrategicPartnerInfo.CompanyAddress1 || "",
          CompanyCity: updatedStrategicPartnerInfo.CompanyCity || "",
          CompanyState: updatedStrategicPartnerInfo.CompanyState || "",
          CompanyZipCode: updatedStrategicPartnerInfo.CompanyZipCode || "",
          CompanyGSTIN: updatedStrategicPartnerInfo.CompanyGSTIN || "",
          InvoiceDate:
            dayjs(dataInvoice.InvoiceDate).format("DD-MMM-YYYY") || "",
        };

        // Open the ProformaInvoice popup with updated data
        openProformaInvoicePopup(updatedDataInvoice);
      } catch (error) {
        console.error("Error opening agreement popup:", error);
        setModal({
          show: true,
          type: "error",
          title: "Error",
          message: "Failed to open agreement. Please try again.",
          autoClose: false,
        });
      } finally {
        setLoading(false);
      }
    },
    [setLoading, allProjectList] // Include allProjectList in dependencies
  );
  const loadProjectList = useCallback(async () => {
    setLoading(true);
    try {
      const [initialResponse] = await Promise.all([
        fetchData({
          target: "listProjects",
          userOrder: "ProjectID",
          rowStart: 0,
          pageSize: 1,
        }),
      ]);

      const totalCount = initialResponse?.total || initialResponse?.length || 0;

      if (totalCount > 0) {
        const [allProducts] = await Promise.all([
          searchfetchQueries({
            target: "listProjects",
            userOrder: "ProjectID",
            rowStart: 0,
            pageSize: totalCount,
          }),
        ]);
        setAllProjectList(allProducts);
      } else {
        setAllProjectList([]);
      }
    } catch (error) {
      console.error("Error loading projects: ", error);
      setAllProjectList([]);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.ProjectID.trim()) {
      newErrors.ProjectID = "Project ID is required";
    }
    if (!formData.InvoiceDate.trim()) {
      newErrors.InvoiceDate = "Invoice Date is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveInvoiceData = async (data) => {
    console.log("data@@", data);
    setLoading(true);
    try {
      const body = isEditMode
        ? {
            target: "updateProcurementInvoices",
            procurementInvoicesInput: {
              InvoiceID: data.InvoiceID || "",
              InvoiceNo: data.InvoiceNo || "",
              ProjectID: data.ProjectID || "",
              InvoiceDate: data.InvoiceDate || "",
              StrategicPartnerID: data.StrategicPartnerID || " ",
              DispatchFrom: data.DispatchFrom || " ",
              ShipTo: data.ShipTo || " ",
              PlaceOfSupply: data.PlaceOfSupply || " ",
              PO: data.PO || " ",
              EInvoiceAckNo: data.EInvoiceAckNo || " ",
              EInvoiceDate: data.EInvoiceDate || " ",
              PackagingNote: data.PackagingNote || " ",
              Unit: data.Unit || "MT",
              PricePerUnit: parseFloat(data.PricePerUnit) || " ",
              Quantity: parseFloat(data.Quantity) || 0,
              SubTotal: parseFloat(data.SubTotal) || 0,
              IgstPercent: parseFloat(data.IgstPercent) || 0,
              CgstPercent: parseFloat(data.CgstPercent) || 0,
              SgstPercent: parseFloat(data.SgstPercent) || 0,
              TotalAmount: parseFloat(data.TotalAmount) || 0,
              EwayBillNo: data.EwayBillNo || " ",
              VehicleNo: data.VehicleNo || " ",
              EInvoiceIRN: data.EInvoiceIRN || " ",
              CreatedBy: data.CreatedBy || " ",
            },
          }
        : {
            target: "createProcurementInvoices",
            procurementInvoicesInput: {
              InvoiceID: data.InvoiceID || " ",
              InvoiceNo: data.InvoiceNo || " ",
              ProjectID: data.ProjectID || " ",
              InvoiceDate: data.InvoiceDate || " ",
              StrategicPartnerID: data.StrategicPartnerID || " ",
              DispatchFrom: data.DispatchFrom || " ",
              ShipTo: data.ShipTo || " ",
              PlaceOfSupply: data.PlaceOfSupply || " ",
              PO: data.PO || " ",
              EInvoiceAckNo: data.EInvoiceAckNo || " ",
              EInvoiceDate: data.EInvoiceDate || " ",
              PackagingNote: data.PackagingNote || " ",
              Unit: data.Unit || "MT",
              PricePerUnit: parseFloat(data.PricePerUnit) || 0,
              Quantity: parseFloat(data.Quantity) || 0,
              SubTotal: parseFloat(data.SubTotal) || 0,
              IgstPercent: parseFloat(data.IgstPercent) || 0,
              CgstPercent: parseFloat(data.CgstPercent) || 0,
              SgstPercent: parseFloat(data.SgstPercent) || 0,
              TotalAmount: parseFloat(data.TotalAmount) || 0,
              EwayBillNo: data.EwayBillNo || " ",
              VehicleNo: data.VehicleNo || " ",
              EInvoiceIRN: data.EInvoiceIRN || " ",
              CreatedBy: data.CreatedBy || " ",
            },
          };
      const result = await postQuery(body);
      if (result) {
        setModal({
          show: true,
          type: "success",
          title: "Success",
          message: `Proforma Invoice Under construction.`,
          autoClose: false,
        });
        await fetchProcurementInvoices(searchValues);
      }
    } catch (error) {
      console.error("Error saving Proforma Invoice:", error);
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message: `Failed to ${isEditMode ? "update" : "add"} Proforma Invoice.`,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("handleSubmit clicked..");
    if (validateForm()) {
      const bodyList = {
        target: "listProcurementInvoices",
        userOrder: "InvoiceID",
        rowStart: 0,
        pageSize: 1,
      };

      const resultList = await fetchData(bodyList);
      let lastInvoiceCount = totalItems;
      if (resultList && (resultList.items || resultList.statusCode === 200)) {
        lastInvoiceCount = resultList.total || resultList.items.length;
      }
      const curreDate = new Date();
      let paddedCount = String(lastInvoiceCount + 1).padStart(3, "0");
      let invNo =
        "AMT/PROF/" + dayjs(curreDate).format("YYYY/MM") + "/" + paddedCount;
      await saveInvoiceData({
        ...formData,
        InvoiceID: isEditMode ? selectedInvoice.InvoiceID : uuidv4(),
        InvoiceNo: isEditMode ? selectedInvoice.InvoiceNo : invNo,
        CreatedBy: isEditMode ? selectedInvoice.CreatedBy : activeUser,
      });
      handleReset();
    }
  };

  const handleReset = () => {
    setFormData(defaultFormData);
    setErrors({});
    setIsEditMode(false);
    setSelectedInvoice(null);
    setShowForm(false);
  };

  const fetchProcurementInvoices = useCallback(
    async (searchValues, newRowStart = rowStart) => {
      setLoading(true);
      try {
        const body = {
          target: "listProcurementInvoices",
          userOrder: "InvoiceID",
          rowStart: newRowStart,
          pageSize: pageSize,
        };
        const response = await fetchData(body);
        console.log("response.items@@", response.items);
        const modifiedItems = (response.items || response).map((item) => ({
          ...item,
          id: item.InvoiceID,
        }));

        setInvoices(modifiedItems);
        setTotalItems(response.total || 0);
      } catch (error) {
        console.error("Error loading projects: ", error);
        setInvoices([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [pageSize, rowStart, setLoading, searchValues]
  );

  const handleRowClick = (row) => {
    setSelectedInvoice(row);
    setIsEditMode(true);
    setShowForm(true);
    setFormData({
      InvoiceNo: row.InvoiceNo || "",
      ProjectID: row.ProjectID || "",
      InvoiceDate: row.InvoiceDate || "",
      StrategicPartnerID: row.StrategicPartnerID || "",
      DispatchFrom: row.DispatchFrom || "",
      ShipTo: row.ShipTo || "",
      PlaceOfSupply: row.PlaceOfSupply || "",
      PO: row.PO || "",
      EInvoiceAckNo: row.EInvoiceAckNo || "",
      EInvoiceDate: row.EInvoiceDate || "",
      PackagingNote: row.PackagingNote || "",
      Unit: row.Unit || "MT",
      PricePerUnit: row.PricePerUnit || "",
      Quantity: row.Quantity || "",
      SubTotal: row.SubTotal || "",
      IgstPercent: row.IgstPercent || "5",
      CgstPercent: row.CgstPercent || "0",
      SgstPercent: row.SgstPercent || "0",
      TotalAmount: row.TotalAmount || "0",
      EwayBillNo: row.EwayBillNo || "",
      VehicleNo: row.VehicleNo || "",
      EInvoiceIRN: row.EInvoiceIRN || "",
    });
  };

  const handleViewInvoice = (event, row) => {
    event.stopPropagation();
    console.log(row);
    setShowForm(false);
    handleInvoicePopup(row);
  };

  useEffect(() => {
    fetchProcurementInvoices(searchValues);
  }, [fetchProcurementInvoices, searchValues]);

  useEffect(() => {
    if (showForm && formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [showForm]);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);

        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_ADMINS")) {
          await Auth.signOut();
          navigate("/");
        } else {
          setActiveUser(user.attributes.email);
        }
        await getParameterFromManager("INR_TO_USD");
      } catch (error) {
        await Auth.signOut();
        navigate("/");
      }
    };

    checkUser();
    loadProjectList();
  }, [navigate, loadProjectList]);

  const handlePageChange = (page) => {
    const newRowStart = (page - 1) * pageSize;
    setRowStart(newRowStart);
    fetchProcurementInvoices(searchValues, newRowStart);
  };

  const handleSearch = (searchTerm) => {
    setSearchValues(searchTerm);
    setRowStart(0);
  };

  const columns = [
    { id: "InvoiceNo", numeric: false, label: "Invoice No" },
    { id: "InvoiceDate", numeric: false, label: "Invoice Date" },
    {
      id: "ProjectID",
      numeric: false,
      label: "Project",
      renderCell: (row) => {
        const pn = allProjectList.find(
          (item) => item.ProjectID === row.ProjectID
        );
        return pn ? pn.ProjectName : row.ProjectID;
      },
    },
    {
      id: "Quantity",
      numeric: false,
      label: "Quantity",
    },
    {
      id: "PricePerUnit",
      numeric: false,
      label: "Price Per Unit",
    },
    {
      id: "TotalAmount",
      numeric: false,
      label: "Total Amount",
    },
    {
      id: "actions",
      numeric: false,
      label: "Actions",
      renderCell: (row) => (
        <Button
          label="View Invoice"
          onClick={(e) => handleViewInvoice(e, row)}
        ></Button>
      ),
    },
  ];

  const Button = ({ label, onClick }) => {
    return (
      <button
        className="btn-action-re"
        onClick={onClick}
        style={{ width: "120px" }}
      >
        {label}
      </button>
    );
  };

  const fields = [
    {
      name: "searchInvoiceNo",
      label: "Invoice No",
      type: "text",
      value: searchValues.searchInvoiceNo,
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, searchInvoiceNo: value })),
    },
  ];
  const currentPage = Math.floor(rowStart / pageSize) + 1;
  return (
    <div className="amtpl-dashboard">
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />
      <div className="card">
        <div className="container-fluid">
          <div className="search-filter4">
            <div className="title4">
              <div className="head-text">Proforma Invoices</div>
            </div>
            <div className="filter4">
              <SearchFilter fields={fields} onSearch={handleSearch} />
            </div>
            <div
              className="head-btn"
              style={{ display: "flex", gap: "10px", alignItems: "center" }}
            >
              <div
                className="submit-btn"
                onClick={() => {
                  setShowForm(true);
                  setIsEditMode(false);
                  setFormData(defaultFormData);
                  setErrors({});
                }}
              >
                Add
              </div>
            </div>
          </div>
          <DataTable
            columns={columns}
            data={invoices}
            total={totalItems}
            title="Proforma Invoices List"
            rowsPerPage={pageSize}
            onRowClick={handleRowClick}
            onPageChange={handlePageChange}
            currentPage={currentPage}
          />
          <div className="table-header">
            {showForm && (
              <div className="header-text">
                <div className="head-text">
                  {isEditMode ? "Modify" : "Add"} Proforma Invoice
                </div>
              </div>
            )}
            <div className="head-btn">
              {showForm && (
                <>
                  <div onClick={handleSubmit} className="submit-btn">
                    {isEditMode ? "Update" : "Submit"}
                  </div>
                  <div onClick={handleReset} className="submit-btn">
                    {isEditMode ? "Cancel" : "Reset"}
                  </div>
                </>
              )}
            </div>
          </div>
          {showForm && (
            <div ref={formRef}>
              <ProformaInvoiceForm
                formData={formData}
                setFormData={setFormData}
                errors={errors}
                setErrors={setErrors}
                isEditMode={isEditMode}
                handleSubmit={handleSubmit}
                handleReset={handleReset}
                allProjectList={allProjectList}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProformaInvoices;
